import {
  Button,
  chakra,
  Divider,
  Heading,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  Tooltip,
  useDisclosure,
  Wrap,
} from "@chakra-ui/react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaLock } from "react-icons/fa";
import { useAuthentication } from "../auth";
import { selectCloudRendering } from "../features/cloudRenderingSlice";
import { enableDebugMode, selectSessionState } from "../features/sessionSlice";
import {
  useAppDispatch,
  useApplicationQuery,
  useAppSelector,
  useSessionDevice,
} from "../hooks";
import { useApplicationBuildQuery } from "../hooks/useApplicationBuildsQuery";
import { useCloudRenderingRegion } from "../hooks/useCloudRenderingRegionsQuery";
import { useVmSizeQuery } from "../hooks/useVmSizesQuery";
import { sendEnableDebug, terminateSession } from "../signalR";
import { BrandedSkeleton } from "./BrandedSkeleton";
import { ConfirmSessionAbortDialog } from "./ConfirmSessionAbortDialog";
import { InputCopyableValue } from "./InputCopyableValue";
import { LatencyDisplay } from "./LatencyDisplay";
import { SessionDuration } from "./SessionDuration";

export function SessionInformation() {
  const abortSessionDialogState = useDisclosure();
  const device = useSessionDevice();
  const currentSession = useAppSelector(selectSessionState);
  const cloudRendering = useAppSelector(selectCloudRendering);
  const { data: cloudRenderingRegion } = useCloudRenderingRegion(
    currentSession.renderRegion,
  );
  const vmSize = useVmSizeQuery(currentSession.vmSize);
  const { t } = useTranslation();
  const { user } = useAuthentication();
  const dispatch = useAppDispatch();
  const applicationBuildQuery = useApplicationBuildQuery(
    currentSession.applicationBuildId,
  );
  const applicationQuery = useApplicationQuery(
    applicationBuildQuery.data?.application,
    {
      enabled: !!applicationBuildQuery.data?.application,
    },
  );

  const [enablingDebugMode, setEnablingDebugMode] = useState(false);

  const enableDebug = useCallback(async () => {
    if (!currentSession.id) throw new Error("No session id");

    if (!(await sendEnableDebug(currentSession.id))) {
      return;
    }

    dispatch(enableDebugMode());
    setEnablingDebugMode(true);

    // takes about 15s to enable debug mode, this helps to get some feedback
    const timeout = setTimeout(() => {
      setEnablingDebugMode(false);
    }, 15000);

    return () => {
      clearTimeout(timeout);
    };
  }, [currentSession.id, dispatch]);

  const latencyMs = useMemo(() => {
    if (
      !currentSession.renderRegion ||
      !cloudRendering.regionsLatency[currentSession.renderRegion]
    )
      return undefined;

    return cloudRendering.regionsLatency[currentSession.renderRegion];
  }, [cloudRendering.regionsLatency, currentSession.renderRegion]);

  return (
    <>
      <Stack px={3} spacing={3}>
        <Stack spacing={1}>
          <Heading as="h6" size="xs">
            {t("session.debug.id")}
          </Heading>
          <InputCopyableValue
            data={currentSession?.id}
            id={"portal-session-id"}
          />
        </Stack>

        <Stack spacing={1}>
          <Heading as="h6" size="xs">
            {t("session.application")}
          </Heading>
          <Wrap>
            <Tooltip
              label={`${t("session.debug.app_api_id")}: ${applicationQuery.data?.id}`}
              hasArrow
            >
              <BrandedSkeleton
                isLoaded={applicationQuery.isSuccess}
                cursor={"help"}
              >
                <Text>
                  {applicationQuery.data?.name || "Fallback Application Title"}
                </Text>
              </BrandedSkeleton>
            </Tooltip>
            <Tooltip
              label={`${t("session.debug.app_version_api_id")}: ${applicationBuildQuery.data?.id}`}
              hasArrow
            >
              <BrandedSkeleton
                isLoaded={applicationBuildQuery.isSuccess}
                cursor={"help"}
              >
                <Text>
                  <chakra.span color="chakra-subtle-text" fontSize={"xs"}>
                    Version:{" "}
                  </chakra.span>

                  {applicationBuildQuery?.data?.version}
                </Text>
              </BrandedSkeleton>
            </Tooltip>
          </Wrap>
        </Stack>

        {currentSession.isCloudRendered && (
          <>
            <Stack spacing={1}>
              <Heading as="h6" size="xs">
                {t("session.debug.streamingServer")}
              </Heading>
              <Tooltip
                label={`${t("session.debug.vm_image")}: ${currentSession?.vmImage}`}
                hasArrow
              >
                <Text>
                  {vmSize?.displayName ?? vmSize?.name ?? "Unknown"}
                  {" - "}
                  {cloudRenderingRegion?.displayName ??
                    currentSession?.renderRegion}{" "}
                  ({currentSession?.renderCloudProvider})
                </Text>
              </Tooltip>
            </Stack>
            <Stack
              pl={3}
              ml={1}
              borderLeft={"1px"}
              borderColor={"chakra-border-color"}
            >
              {currentSession?.ipAddress && (
                <Stack spacing={1}>
                  <Heading as="h6" size="xs">
                    {t("session.debug.serverIp")}
                  </Heading>
                  <InputCopyableValue
                    data={currentSession?.ipAddress}
                    id={"portal-session-ip-address"}
                  />
                </Stack>
              )}
              <Stack spacing={1}>
                <Heading as="h6" size="xs">
                  {t("session.debug.streamingProtocols")}
                </Heading>
                <Wrap>
                  {currentSession.isCloudRenderedVr && (
                    <Tag variant="outline" size="sm">
                      <TagLabel>
                        CloudXR{" "}
                        <Text
                          as="span"
                          color="chakra-subtle-text"
                          fontSize={"small"}
                        >
                          (NVIDIA)
                        </Text>
                      </TagLabel>
                    </Tag>
                  )}
                  {currentSession.streamingGatewayEndpoint && (
                    <Tag variant="outline" size="sm">
                      <TagLabel>
                        DCV{" "}
                        <Text
                          as="span"
                          color="chakra-subtle-text"
                          fontSize={"small"}
                        >
                          (TCP)
                        </Text>
                      </TagLabel>
                    </Tag>
                  )}
                  <Tag variant="outline" size="sm">
                    <TagLabel>
                      Innoactive{" "}
                      <Text
                        as="span"
                        color="chakra-subtle-text"
                        fontSize={"small"}
                      >
                        (WebRTC)
                      </Text>
                    </TagLabel>
                  </Tag>
                </Wrap>
              </Stack>
              {currentSession.isCloudRendered && latencyMs && (
                <Stack spacing={1}>
                  <Heading as="h6" size="xs">
                    {t("session.debug.latency")}
                  </Heading>
                  <Text fontSize={"sm"}>
                    <LatencyDisplay latency={latencyMs} fontWeight={"normal"} />
                  </Text>
                </Stack>
              )}
            </Stack>
          </>
        )}
        {currentSession.isCloudRenderedVr && device && (
          <Stack spacing={1}>
            {currentSession.xrEncryption && (
              <Stack spacing={1}>
                <Heading as="h6" size="xs">
                  {t("session.debug.xr_device")}
                </Heading>
                <Wrap>
                  <Text>{device.name}</Text>
                  {currentSession.xrEncryption && (
                    <Tooltip
                      hasArrow
                      label={
                        currentSession.isUsingXrGateway
                          ? t("session.debug.xr_gateway_used")
                          : undefined
                      }
                    >
                      <Tag variant="outline" size="sm" colorScheme={"green"}>
                        <TagLeftIcon as={FaLock} />
                        <TagLabel>
                          {t("session.debug.xr_encryption_enabled")}
                        </TagLabel>
                      </Tag>
                    </Tooltip>
                  )}
                </Wrap>
              </Stack>
            )}
          </Stack>
        )}

        {currentSession.isActive && (
          <Stack spacing={1}>
            <Heading as="h6" size="xs">
              {t("session.debug.duration")}
            </Heading>
            <Text>
              <SessionDuration />
            </Text>
          </Stack>
        )}

        {user?.global_permissions.includes("core.remote_rendering.can_debug") &&
          currentSession.ipAddress &&
          currentSession.isCloudRendered && (
            <>
              <Divider />
              <Tooltip
                label={
                  currentSession.debugModeEnabled
                    ? t("session.debug.debug_enable_tooltip")
                    : t("session.debug.enable_debug_tooltip")
                }
              >
                <Button
                  onClick={() => enableDebug()}
                  isDisabled={currentSession.debugModeEnabled}
                  isLoading={enablingDebugMode}
                >
                  {t("session.debug.enable_debug")}
                </Button>
              </Tooltip>
            </>
          )}

        <Divider />
        <Button onClick={abortSessionDialogState.onOpen} colorScheme="red">
          {t("session.end")}
        </Button>
      </Stack>
      <ConfirmSessionAbortDialog
        isOpen={abortSessionDialogState.isOpen}
        onClose={abortSessionDialogState.onClose}
        onConfirm={() => {
          currentSession.id && terminateSession(currentSession.id);
        }}
      />
    </>
  );
}
